// src/components/block/liveStreamManagement/LiveStreamManagerBootstrap.tsx
//
// Ce fichier est un composant d'amorçage (bootstrap). Son seul rôle est de préparer et centraliser 
// les références partagées avant de les injecter dans LiveStreamManager.

import   React                         , 
       { useRef                        }  from 'react';

import { useMediaStream                }  from '../../../contexts/MediaStreamContext';
import { ProductionControlRoomProvider ,
         useProductionControlRoom      }  from '../../../contexts/ProductionControlRoomContext';

import { SourceRegistryProvider        }  from '../../../contexts/liveStreamManagment/SourceRegistryContext';
import { AudioMixerProvider            }  from '../../../contexts/liveStreamManagment/AudioMixerContext';

import { SignalingRefs                 }  from '../../../dataTypes/liveStreamManagment/types_signaling';
import { BroadcastingChannel           ,
         StreamSource                  }  from '../../../dataTypes/broadcastingManagment/types';

import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';


import   LiveStreamManager                from './LiveStreamManager';

interface Props {
  broadcastingChannel          : BroadcastingChannel;
  streamSource?                : StreamSource;
  defaultStreamSource?         : StreamSource;
  onStreamSourceChange?        : (source: StreamSource) => void;
  onDefaultStreamSourceChange? : (source: StreamSource) => void;
  onStopStreaming?              : () => void;
  hlsVolume?                   : number;
  hlsMuted?                    : boolean;
  onHlsVolumeChange?           : (v: number) => void;
  onHlsMutedChange?            : (m: boolean) => void;
  onRegisterStart?             : (fn: () => void) => void;
  onRegisterStop?              : (fn: () => void) => void;
  onConfirmStreamSource?       : (source: StreamSource) => void;
  hlsMonitorStream?: MediaStream | null;
  hlsAudioRegistry?  : AudioSourceRegistry;
  hlsAudioSourceId?  : string;
  onHlsReset?: () => void;
  channelSignalConfirmed? : boolean;
  onLiveStatusChange? : (status: { isRecording: boolean; isStreaming: boolean }) => void;
}

// ── Inner wrapper — doit être DANS le Provider pour accéder au contexte ───────

const LiveStreamManagerInner: React.FC<Props> = ({
  broadcastingChannel,
  streamSource,
  defaultStreamSource,
  onStreamSourceChange,
  onDefaultStreamSourceChange,
  onStopStreaming,
  hlsVolume,
  hlsMuted,
  onHlsVolumeChange,
  onHlsMutedChange,
  onRegisterStart,
  onRegisterStop,
  onConfirmStreamSource,
  hlsMonitorStream,
  hlsAudioRegistry,
  hlsAudioSourceId,
  onHlsReset,
  channelSignalConfirmed,
  onLiveStatusChange
}) => {

  const { peerConnectionRef }    = useMediaStream();
  const { remoteDeviceInfoRef }  = useProductionControlRoom();

  const signalingWsRef           = useRef<WebSocket | null>(null);
  const iceCandidateBufferRef    = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const remoteDescSetRef         = useRef<Map<string, boolean>>(new Map());
  const remoteConnectionsRef     = useRef<Map<string, any>>(new Map());
  const deviceSupportedCodecsRef = useRef<Map<string, string[]>>(new Map());
  const requiredCodecRef         = useRef<{ codec: string; profile: string } | null>(null);
  const whipOfferSdpRef          = useRef<string | undefined>(undefined);
  const whipAnswerSdpRef         = useRef<string | undefined>(undefined);
  const whipStatsIntervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsReconnectTimeoutRef    = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const wsHbIntervalRef          = useRef<ReturnType<typeof setInterval> | null>(null);
  const wsPongTimeoutRef         = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const currentStreamRef         = useRef<MediaStream | null>(null);
  const canvasStreamRef          = useRef<MediaStream | null>(null);
  const audioMixerDestRef        = useRef<MediaStreamAudioDestinationNode | null>(null);

  const signalingRefs: SignalingRefs = {
    wsRef                  : signalingWsRef,
    wsReconnectTimeoutRef,
    wsHbIntervalRef,
    wsPongTimeoutRef,
    peerConnectionRef,
    iceCandidateBufferRef,
    remoteDescSetRef,
    remoteConnectionsRef,
    remoteDeviceInfoRef,
    deviceSupportedCodecsRef,
    requiredCodecRef,
    whipOfferSdpRef,
    whipAnswerSdpRef,
    whipStatsIntervalRef,
    currentStreamRef,
    canvasStreamRef,
    audioMixerDestRef,
  };

  return (
    <LiveStreamManager
      _sharedRefs                 = {signalingRefs}
      broadcastingChannel         = {broadcastingChannel}
      streamSource                = {streamSource}
      defaultStreamSource         = {defaultStreamSource}
      onStreamSourceChange        = {onStreamSourceChange}
      onDefaultStreamSourceChange = {onDefaultStreamSourceChange}
      onStopStreaming              = {onStopStreaming}
      hlsVolume                   = {hlsVolume}
      hlsMuted                    = {hlsMuted}
      onHlsVolumeChange           = {onHlsVolumeChange}
      onHlsMutedChange            = {onHlsMutedChange}
      onRegisterStart             = {onRegisterStart}
      onRegisterStop              = {onRegisterStop}
      onConfirmStreamSource       = {onConfirmStreamSource}
      hlsMonitorStream          = {hlsMonitorStream}
	  hlsAudioRegistry = {hlsAudioRegistry}
      hlsAudioSourceId       = {hlsAudioSourceId}
	  onHlsReset = {onHlsReset}
      channelSignalConfirmed = {channelSignalConfirmed}
      onLiveStatusChange = {onLiveStatusChange}
    />
  );
};

// ── Export public — le Provider enveloppe l'inner ─────────────────────────────

const LiveStreamManagerBootstrap: React.FC<Props> = ({
  broadcastingChannel,
  streamSource,
  defaultStreamSource,
  onStreamSourceChange,
  onDefaultStreamSourceChange,
  onStopStreaming,
  hlsVolume,
  hlsMuted,
  onHlsVolumeChange,
  onHlsMutedChange,
  onRegisterStart,
  onRegisterStop,
  onConfirmStreamSource,
  hlsMonitorStream,
  hlsAudioRegistry,
  hlsAudioSourceId,
  onHlsReset,
  channelSignalConfirmed,
  onLiveStatusChange
}) => (
  // `SourceRegistryProvider` a quitté cet emplacement le 2026-08-28 : il est monté à la
  // racine (index.tsx), au-dessus de la `key` par chaîne. Ici, il était détruit et
  // recréé à chaque bascule, ce qui coupait la caméra locale.
  <AudioMixerProvider>
      <ProductionControlRoomProvider>
        <LiveStreamManagerInner
          broadcastingChannel         = {broadcastingChannel}
          streamSource                = {streamSource}
          defaultStreamSource         = {defaultStreamSource}
          onStreamSourceChange        = {onStreamSourceChange}
          onDefaultStreamSourceChange = {onDefaultStreamSourceChange}
          onStopStreaming             = {onStopStreaming}
          hlsVolume                   = {hlsVolume}
          hlsMuted                    = {hlsMuted}
          onHlsVolumeChange           = {onHlsVolumeChange}
          onHlsMutedChange            = {onHlsMutedChange}
          onRegisterStart             = {onRegisterStart}
          onRegisterStop              = {onRegisterStop}
          onConfirmStreamSource       = {onConfirmStreamSource}
          hlsMonitorStream          = {hlsMonitorStream}
		  hlsAudioRegistry = {hlsAudioRegistry}
          hlsAudioSourceId       = {hlsAudioSourceId}
	  	  onHlsReset = {onHlsReset}
          channelSignalConfirmed = {channelSignalConfirmed}
          onLiveStatusChange = {onLiveStatusChange}
        />
      </ProductionControlRoomProvider>
  </AudioMixerProvider>
);

export default LiveStreamManagerBootstrap;